import { z } from 'zod'
import { objectToSnake } from 'ts-case-convert'
import { API, handleError, handleFormError, handleZodError } from './api'
import { COUNTRIES } from '../constants'
import { cleanObject } from '../helpers'
import {
  clientDashboardSchema,
  clientExtendedSchema,
  clientMonogramSchema,
  clientSchema,
  garmentRenderSchema,
  garmentTypeSchema,
  uploadedImageSchema,
} from './schemas'
import { fileSchema, imageSchema } from './schemas/generic'

//* ---------------- APIS ---------------- *//
export async function getClients(rawParams?: Record<string, unknown>) {
  try {
    const cleanData = cleanObject({ ...rawParams })
    const checkedData = getClientParamsSchema.parse(cleanData)
    const params = cleanObject(checkedData)

    const response = await API.get('customers/dashboard', { params })
    const clients = z.array(clientDashboardSchema).parse(response.data)

    return { clients, totalClients: response.headers.total }
  } catch (error) {
    return handleError(error, 'Clients')
  }
}

export async function getExtendedClients(rawParams?: Record<string, unknown>) {
  try {
    const cleanData = cleanObject({ ...rawParams })
    const checkedData = getClientParamsSchema.parse(cleanData)
    const params = cleanObject(checkedData)

    const response = await API.get('customers', { params: { ...params, extended: '1' } })
    const clients = z.array(clientExtendedSchema).parse(response.data)

    return { clients, totalClients: response.headers.total }
  } catch (error) {
    return handleError(error, 'Extended Clients')
  }
}

export async function getClient(id: number) {
  try {
    const response = await API.get(`customers/${id}`)
    const client = clientExtendedSchema.parse(response.data)

    return client
  } catch (error) {
    return handleError(error, 'Client')
  }
}

export async function createClient(data: Record<string, unknown>) {
  try {
    const cleanData = cleanObject({ ...data })
    const checkedData = newClientSchema.safeParse(cleanData)

    if (checkedData.success) {
      const response = await API.post('customers', checkedData.data)
      const client = clientSchema.parse(response.data)
      return { client }
    } else {
      const errors = handleZodError(checkedData.error)
      return { errors }
    }
  } catch (error) {
    return handleError(error, 'Create Client')
  }
}

export async function updateClient(id: number, data: Record<string, unknown>) {
  try {
    const cleanData = cleanObject({ ...data })
    const checkedData = newClientSchema.safeParse(cleanData)

    if (checkedData.success) {
      const response = await API.put(`customers/${id}`, checkedData.data)
      const client = clientSchema.parse(response.data)
      return { client }
    } else {
      const errors = handleZodError(checkedData.error)
      return { errors }
    }
  } catch (error) {
    return handleError(error, 'Update Client')
  }
}

export async function deleteClient(id: number, data: Record<string, unknown>) {
  try {
    await API.put(`customers/${id}`, { ...data, customer_status: 'deleted' })
    return { success: true }
  } catch (error) {
    return handleError(error, 'Delete Client')
  }
}

export async function getClientGarmentImages(id: number, data: Record<string, unknown>) {
  try {
    const params = getClientGarmentImagesSchema.parse(data)
    const response = await API.get(`customers/${id}/garment_images`, { params })
    const garments = z.array(garmentRenderSchema).parse(response.data)

    return garments
  } catch (error) {
    return handleError(error, 'Client Garment Images')
  }
}

export async function getClientImages(id: number) {
  try {
    const response = await API.get(`customers/${id}/images`)
    return z.array(uploadedImageSchema).parse(response.data)
  } catch (error) {
    return handleError(error, 'Customer Images')
  }
}

export async function getClientGarmentTypes(id: number, data: Record<string, unknown>) {
  try {
    const params = getClientGarmentImagesSchema.parse(data)
    const response = await API.get(`customers/${id}/garment_types`, { params })
    const garmentTypes = z.array(garmentTypeSchema).parse(response.data)

    return garmentTypes
  } catch (error) {
    return handleError(error, 'Client Garment Types')
  }
}

/** Client Monograms */
export async function getClientMonograms(rawParams: Record<string, unknown>) {
  try {
    const params = getClientMonogramsSchema.parse(cleanObject({ ...rawParams }))

    const response = await API.get('customer_monograms', { params })
    const monograms = z.array(clientMonogramSchema).parse(response.data)

    return { monograms, totalMonograms: response.headers.total }
  } catch (error) {
    return handleError(error, 'Client Monograms')
  }
}

export async function getClientMonogram(id: number) {
  try {
    const response = await API.get(`customer_monograms/${id}`)
    const monogram = clientMonogramSchema.parse(response.data)

    return { monogram }
  } catch (error) {
    return handleError(error, 'Client Monogram')
  }
}

export async function createClientMonogram(rawParams: Record<string, unknown>) {
  try {
    const params = createClientMonogramSchema.parse(cleanObject(rawParams))
    await API.post('customer_monograms', params)
    return { error: false, message: 'Client Monogram Created' }
  } catch (error) {
    return handleError(error, 'Create Client Monogram')
  }
}

/**
 * Update a Client Signature Monogram
 * @param id the monogram id
 * @param data FormData from parseFormData (MUST be FormData from a Form using enctype='multipart/form-data')
 * @returns
 */
export async function updateClientMonogram(id: number, data: { [key: string]: FormDataEntryValue }) {
  try {
    const formData = updateClientMonogramsSchema.parse(data)

    await API.put(`customer_monograms/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return { error: false, message: 'Customer Monogram updated successfully' }
  } catch (error) {
    return handleFormError(error, 'Update Client Monogram')
  }
}

export async function deleteClientMonogram(monogramId: number) {
  try {
    await API.delete(`customer_monograms/${monogramId}`)
    return { error: false, message: 'Customer Monogram deleted successfully.' }
  } catch (error) {
    return handleError(error, 'Delete Customer Monogram')
  }
}

//* ---------------- PARAMS ---------------- *//
const getClientParamsSchema = z
  .object({
    q: z.string().optional(),
    haveBought: z.union([z.literal('true'), z.literal('false')]).optional(),
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
    garmentTypes: z.string().optional(),
    page: z.coerce.number().default(1),
    perPage: z.coerce.number().default(100),
    dealerId: z.coerce.number().optional(),
  })
  .transform(({ garmentTypes, ...rest }) => ({
    ...rest,
    garmentType: garmentTypes?.split(','),
  }))
  .transform(objectToSnake)

const newClientSchema = z
  .object({
    name: z.string({ required_error: 'Name is required' }),
    gender: z.union([z.literal('male'), z.literal('female')]).optional(),
    company: z.string().nullable().default(null),
    nameLabel: z.string().nullable().default(null),
    laundryMarker: z.string().nullable().default(null),
    monogram: z.string().nullable().default(null),
    addressType: z.union([z.literal('residential'), z.literal('commercial')]).optional(),
    address1: z.string().nullable().default(null),
    address2: z.string().nullable().default(null),
    city: z.string().nullable().default(null),
    state: z.string().nullable().default(null),
    zip: z.string().nullable().default(null),
    country: z.enum(COUNTRIES).optional(),
    cellPhone: z.string().nullable().default(null),
    homePhone: z.string().nullable().default(null),
    email: z.string().email().nullable().default(null),
  })
  .transform(objectToSnake)
  .transform(
    ({ company, address_1, address_2, city, state, zip, country, cell_phone, home_phone, email, ...data }) => ({
      customer_company: company,
      customer_address1: address_1,
      customer_address2: address_2,
      customer_city: city,
      customer_state: state,
      customer_zip: zip,
      customer_country: country,
      customer_cell: cell_phone,
      customer_home_phone: home_phone,
      customer_email: email,
      ...data,
    }),
  )

const getClientGarmentImagesSchema = z
  .object({
    garmentType: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    isHidden: z.enum(['0', '1']).optional(),
  })
  .transform(objectToSnake)
  .transform(({ garment_type, ...data }) => ({
    ...data,
    garment_type: garment_type === 'all' ? undefined : garment_type,
  }))

/** Client Monograms Params Schemas */
const createClientMonogramSchema = z
  .object({
    customerId: z.coerce.number(),
  })
  .transform(objectToSnake)

const getClientMonogramsSchema = z
  .object({
    dealerId: z.coerce.number().optional(),
    customerId: z.coerce.number().optional(),
    page: z.coerce.number().default(1),
    perPage: z.coerce.number().default(100),
  })
  .transform(objectToSnake)

// NOTE: FormData cannot be transformed by `objectToSnake`
const updateClientMonogramsSchema = z.object({
  image: imageSchema.optional(),
  monogram: fileSchema.optional(),
  active: z.enum(['0', '1']).optional(),
})

//* ---------------- TYPES ---------------- *//
